import { planLimitSentence } from '../../utils/planLimit';
import { cellText, elementLabel } from '../../utils/spreadsheet/columns';

/**
 * The words of Import a spreadsheet (KPR-1899), and a refusal in a person's
 * terms. Ingest answers a machine: `expected date (YYYY-MM-DD), got "13/45/24"`
 * is true, and of no use to someone fixing a column, so each code here says
 * what is wrong with the value in the file. A code with no words of its own
 * shows the server's sentence, which the row validator writes to be read.
 */

export const STEP_TITLES = ['Choose a file', 'Match columns', 'Check', 'Import'];

export const LEAVE_OUT = 'Leave out';
export const CHOOSE_ELEMENT = 'Choose an element';

const QUOTE_MAX = 60;
export const quoted = value => {
	const text = String(value ?? '');
	return `“${text.length > QUOTE_MAX ? `${text.slice(0, QUOTE_MAX - 1)}…` : text}”`;
};

/** A count with its thousands: 1,240 rows. Zero is a word of the caller's. */
export const countWords = (count, singular, plural = `${singular}s`) => `${Number(count).toLocaleString('en-US')} ${count === 1 ? singular : plural}`;

const choicesLine = element => {
	const labels = (element?.choices || []).map(choice => choice?.label || choice?.value).filter(Boolean);
	if (!labels.length) return '';
	const shown = labels.slice(0, 6).join(', ');
	return labels.length > 6 ? ` The choices include ${shown}.` : ` The choices are ${shown}.`;
};

const TYPE_WORDS = {
	date: 'a date keepr can read',
	'date-time': 'a date and time keepr can read',
	time: 'a time keepr can read',
	number: 'a number',
	decimal: 'a number',
	integer: 'a whole number',
	boolean: 'a yes or no',
	rating: 'a rating on this scale',
	measurement: 'a measurement keepr can read',
	currency: 'an amount keepr can read',
};

const NOT_A = {
	invalid_phone: 'a phone number keepr can read',
	invalid_email: 'an email address',
	invalid_url: 'a link keepr accepts',
	invalid_color: 'a color keepr can read',
	invalid_location: 'an address or coordinates keepr can read',
};

const label = (element, fallback) => (element ? elementLabel(element) : fallback);

/** A row's refusal as one sentence. `error`: ingest's, or a name the browser could not settle. */
export function errorWords(error, { element = null, value = '' } = {}) {
	const shown = quoted(value);
	const name = label(element, error.element || 'This element');
	switch (error.code) {
		case 'name_not_found':
			return element?.dataType === 'user' ? `No one called ${shown} is in this collection.` : `No record called ${shown} was found for ${name}.`;
		case 'name_ambiguous':
			return element?.dataType === 'user'
				? `${error.count} people are called ${shown}, so keepr cannot tell which one.`
				: `${error.count} records are called ${shown}, so keepr cannot tell which one.`;
		case 'name_unchecked':
			return `keepr looks up a limited number of names in one check, and did not reach ${shown}. Import the rows it did, then this one on its own.`;
		case 'invalid_choice':
			return `${shown} is not one of the choices for ${name}.${choicesLine(element)}`;
		case 'type':
			if (TYPE_WORDS[element?.dataType]) return `${shown} is not ${TYPE_WORDS[element.dataType]}.`;
			return error.message;
		case 'invalid_phone':
		case 'invalid_email':
		case 'invalid_url':
		case 'invalid_color':
		case 'invalid_location':
			return `${shown} is not ${NOT_A[error.code]}.`;
		case 'invalid_lookup':
		case 'lookup_not_found':
			return `No record called ${shown} was found for ${name}, or you cannot see it.`;
		case 'ref_wrong_card':
			return `${shown} is on a card ${name} does not take.`;
		case 'lookup_filtered_out':
			return `${name} does not offer ${shown}.`;
		case 'user_not_found':
			return `No one with an active account matches ${shown}.`;
		case 'required':
			return `${name} is required, and this row has nothing for it.`;
		case 'too_long':
			return `This is longer than the ${Number(error.limit).toLocaleString('en-US')} characters ${name} holds.`;
		case 'duplicate_value':
			return error.title ? `${error.title} already has ${shown}, and ${name} takes a value only once.` : `Another item already has ${shown}, and ${name} takes a value only once.`;
		case 'element_read_only':
			return `You can see ${name} but not change it.`;
		case 'unknown_element':
			return `${name} is not on this card any more. Go back and match this column again.`;
		case 'forbidden_card':
			return 'You cannot add items to this card.';
		case 'card_unknown':
		case 'card_not_allowed':
			return 'This card does not take items here any more.';
		case 'plan_limit':
			return planLimitSentence(error);
		case 'attestation_required':
			return 'This row needs your signature.';
		default:
			return error.message || 'keepr refused this row.';
	}
}

/** An error that says the same as a problem the browser already found: the name left out of a row. */
const repeatsProblem = (error, problems) => error.code === 'required' && problems.some(problem => problem.element === error.element);

const rowIndexes = new WeakMap();

/** A sheet's row by its number: a sheet of failures looks up every one of them. */
function cellsOf(sheet, number) {
	if (!rowIndexes.has(sheet)) rowIndexes.set(sheet, new Map(sheet.rows.map(row => [row.number, row.cells])));
	return rowIndexes.get(sheet).get(number) || [];
}

/** What the download says of a row that was never sent. */
export const unsentLine = row => ({ key: `${row.number}.u`, number: row.number, column: '', value: '', words: 'Not imported yet.' });

/**
 * One failed row as lines: [{ key, number, column, value, words }], one per
 * thing to fix, what the browser found first. `context` is
 * { sheet, columnsByElement, elementsByName }.
 */
export function failureLines({ row, errors = [] }, { sheet, columnsByElement, elementsByName }) {
	const cells = cellsOf(sheet, row.number);
	const problems = row.problems || [];
	const lines = problems.map((problem, i) => ({
		key: `${row.number}.p${i}`,
		number: row.number,
		column: problem.column,
		value: problem.value,
		words: errorWords(problem, { element: elementsByName[problem.element], value: problem.value }),
	}));
	errors.filter(error => !repeatsProblem(error, problems)).forEach((error, i) => {
		const element = error.element ? elementsByName[error.element] : null;
		const column = error.element ? columnsByElement[error.element] : null;
		const value = column ? cellText(cells[column.index]).trim() : '';
		lines.push({
			key: `${row.number}.e${i}`,
			number: row.number,
			column: column?.name || (element ? elementLabel(element) : ''),
			value,
			words: errorWords(error, { element, value }),
		});
	});
	return lines;
}

/** The sentence over the check's table. */
export function checkSummary({ ready, failed, empty }) {
	const parts = [];
	if (ready) parts.push(`${countWords(ready, 'row')} ${ready === 1 ? 'is' : 'are'} ready to import.`);
	else parts.push('No row is ready to import.');
	if (failed) parts.push(`${countWords(failed, 'row')} ${failed === 1 ? 'needs' : 'need'} a fix.`);
	if (empty) parts.push(`${countWords(empty, 'row')} with nothing in the matched columns ${empty === 1 ? 'is' : 'are'} skipped.`);
	return parts.join(' ');
}

/** The sentence over the last step. `left` counts every row that did not become an item. */
export function resultSummary({ landed, left, cardName }) {
	const parts = [landed ? `${countWords(landed, 'item')} added to ${cardName}.` : `Nothing was added to ${cardName}.`];
	if (left) parts.push(`${countWords(left, 'row')} ${left === 1 ? 'was' : 'were'} left out.`);
	return parts.join(' ');
}
